import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Sparkles, Loader2, Crown, AlertTriangle, Lightbulb, RefreshCw, ArrowLeft } from 'lucide-react';
import { api, fileUrl } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { useI18n } from '../i18n';
import AiCaution from '../components/AiCaution';
import { toast } from 'sonner';

export default function WeeklyPlan() {
    const { user } = useAuth();
    const { t, locale } = useI18n();
    const navigate = useNavigate();
    const [plan, setPlan] = useState(null);
    const [generating, setGenerating] = useState(false);

    useEffect(() => {
        api.get('/ai/weekly-plan').then((r) => {
            if (r.data?.plan) setPlan(r.data.plan);
        }).catch(() => {});
    }, []);

    const generate = async () => {
        setGenerating(true);
        try {
            const r = await api.post('/ai/weekly-plan');
            setPlan(r.data);
            toast.success('Plan ready! 🌱');
        } catch (err) {
            const d = err?.response?.data?.detail;
            toast.error(typeof d === 'string' ? d : 'AI failed');
        } finally { setGenerating(false); }
    };

    if (!user?.premium && !user?.ai_payg_available) {
        return (
            <div className="px-5 pt-12 pb-32 max-w-2xl mx-auto">
                <button onClick={() => navigate(-1)} className="inline-flex items-center gap-1 text-sm text-brand-ink3 hover:text-brand-ink mb-6"><ArrowLeft size={14} /> {t('common.back')}</button>
                <div className="bg-brand-card border border-brand-border rounded-3xl p-8 text-center">
                    <Crown size={40} className="mx-auto text-brand-ochre mb-4" strokeWidth={1.5} />
                    <h1 className="font-serif text-2xl text-brand-ink">{t('plan.locked')}</h1>
                    <p className="text-brand-ink2 mt-2 text-sm">{t('plan.locked_sub')}</p>
                    <button onClick={() => navigate('/premium')} className="mt-6 bg-brand-green text-white px-6 py-3 rounded-full font-medium">
                        {t('premium.cta')}
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="px-5 pt-10 pb-32 max-w-2xl mx-auto">
            <div className="flex items-start justify-between mb-6">
                <div>
                    <p className="text-[10px] uppercase tracking-widest font-semibold text-brand-ink3 inline-flex items-center gap-1">
                        <Sparkles size={11} /> AI · Claude
                    </p>
                    <h1 className="font-serif text-3xl text-brand-ink mt-1">{t('plan.title')}</h1>
                    <p className="text-sm text-brand-ink2 mt-1">{t('plan.sub')}</p>
                </div>
            </div>

            <AiCaution className="mb-6" />

            {!plan && !generating && (
                <div className="bg-brand-card border border-dashed border-brand-border rounded-3xl p-10 text-center">
                    <Sparkles size={36} className="mx-auto text-brand-green mb-3" strokeWidth={1.5} />
                    <p className="text-sm text-brand-ink2 mb-5">{t('plan.empty')}</p>
                    <button data-testid="btn-generate-plan" onClick={generate} className="bg-brand-green hover:bg-brand-greenDark text-white px-6 py-3 rounded-full font-medium inline-flex items-center gap-2">
                        <Sparkles size={15} /> {t('plan.generate')}
                    </button>
                </div>
            )}

            {generating && (
                <div className="bg-brand-card border border-brand-border rounded-3xl p-10 text-center">
                    <Loader2 size={32} className="mx-auto animate-spin text-brand-green mb-3" />
                    <p className="text-sm text-brand-ink2">{t('plan.generating')}</p>
                </div>
            )}

            {plan && (
                <div className="space-y-4 stagger">
                    {plan.focus && (
                        <div className="bg-brand-green text-white rounded-3xl p-5">
                            <div className="text-[10px] uppercase tracking-widest font-semibold text-brand-bg/70">{t('plan.focus')}</div>
                            <h2 className="font-serif text-xl mt-1">{plan.focus}</h2>
                            {plan.summary && <p className="text-sm text-brand-bg/85 mt-2 leading-relaxed">{plan.summary}</p>}
                        </div>
                    )}

                    {plan.warnings?.length > 0 && (
                        <div className="bg-brand-terracotta/10 border border-brand-terracotta/30 rounded-2xl p-4">
                            <div className="text-[10px] uppercase tracking-widest font-semibold text-brand-terracotta inline-flex items-center gap-1 mb-2"><AlertTriangle size={11} /> {t('plan.warnings')}</div>
                            <ul className="space-y-1.5">
                                {plan.warnings.map((w, i) => <li key={`w-${i}-${String(w).slice(0,20)}`} className="text-sm text-brand-ink2">• {w}</li>)}
                            </ul>
                        </div>
                    )}

                    {plan.plant_alerts?.length > 0 && (
                        <section className="space-y-2" aria-labelledby="plant-alerts-title">
                            <h2 id="plant-alerts-title" className="text-[10px] uppercase tracking-widest font-semibold text-brand-ink3">{locale === 'da' ? 'Planter der kræver opmærksomhed' : 'Plants needing attention'}</h2>
                            {plan.plant_alerts.map((alert) => (
                                <button key={alert.plant_id} onClick={() => navigate('/garden-doctor', { state: { plantId: alert.plant_id, plantName: alert.name } })} className="w-full bg-brand-card border border-brand-terracotta/25 rounded-2xl p-3 flex items-center gap-3 text-left hover:border-brand-terracotta/60">
                                    {alert.image_path ? <img src={fileUrl(alert.image_path)} alt="" className="w-16 h-16 rounded-2xl object-cover border border-brand-border" /> : <div className="w-16 h-16 rounded-2xl bg-brand-bg2 flex items-center justify-center text-2xl">{alert.emoji}</div>}
                                    <div className="flex-1 min-w-0"><div className="flex items-center justify-between gap-2"><span className="font-serif text-base text-brand-ink truncate">{alert.name}</span><span className="text-xs font-semibold text-brand-terracotta">{alert.health}%</span></div>{alert.summary && <p className="text-xs text-brand-ink2 mt-0.5 line-clamp-2">{alert.summary}</p>}<p className="text-[10px] text-brand-green mt-1">{locale === 'da' ? 'Åbn i Havedoktoren →' : 'Open in Garden Doctor →'}</p></div>
                                </button>
                            ))}
                        </section>
                    )}

                    {Array.isArray(plan.days) && plan.days.map((d, i) => (
                        <div data-testid={`plan-day-${i}`} key={`day-${d.date || i}-${d.day || ''}`} className="bg-brand-card border border-brand-border rounded-2xl p-5">
                            <div className="flex items-baseline justify-between mb-2">
                                <h3 className="font-serif text-lg text-brand-ink">{d.day}</h3>
                                <span className="text-[10px] uppercase tracking-widest text-brand-ink3 font-semibold">{d.date}</span>
                            </div>
                            {d.weather_note && <p className="text-xs text-brand-ink3 italic mb-3">{d.weather_note}</p>}
                            <ul className="space-y-1.5">
                                {(d.tasks || []).map((task, j) => (
                                    <li key={`task-${j}-${String(task).slice(0,20)}`} className="text-sm text-brand-ink flex items-start gap-2">
                                        <span className="text-brand-green mt-0.5">▸</span>
                                        <span>{task}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    ))}

                    {plan.tips?.length > 0 && (
                        <div className="bg-brand-ochre/10 border border-brand-ochre/30 rounded-2xl p-4">
                            <div className="text-[10px] uppercase tracking-widest font-semibold text-brand-ochre inline-flex items-center gap-1 mb-2"><Lightbulb size={11} /> {t('plan.tips')}</div>
                            <ul className="space-y-1.5">
                                {plan.tips.map((tip, i) => <li key={`tip-${i}-${String(tip).slice(0,20)}`} className="text-sm text-brand-ink2">• {tip}</li>)}
                            </ul>
                        </div>
                    )}

                    {plan.raw && <p className="text-sm text-brand-ink2 whitespace-pre-wrap">{plan.raw}</p>}

                    <button data-testid="btn-regenerate-plan" onClick={generate} disabled={generating} className="w-full bg-brand-card border border-brand-border hover:bg-brand-bg2 text-brand-ink py-3 rounded-full font-medium inline-flex items-center justify-center gap-2 disabled:opacity-50">
                        <RefreshCw size={14} strokeWidth={1.8} /> {t('plan.generate')}
                    </button>
                </div>
            )}
        </div>
    );
}
